import { userQueryKeys } from '@/config/queryKeys';
import { canManageSuperAdminRole } from '@/config/users';
import { adminRoles } from '@/constants';
import { checkAndRedirectForOrgAdminAccess } from '@/helpers';
import { useRoles, useRouteProtection } from '@/hooks';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getDateAsString } from '@/lib';
import { apiClient } from '@/lib/api-client';
import { ROLE_HIERARCHY } from '@/lib/roles';
import { useAppStore } from '@/stores/app';
import { Role, UserPublic } from '@/types';
import ErrorMessage from '@components/ErrorMessage';
import { FullLoading } from '@components/Loaders';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import Badge from '@nautilusbio/nb-design-system/Badge';
import Button from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import pageStyles from '../../pages/Users/styles.module.css';
import SuperAdminGlobalTable from '../../pages/Users/SuperAdminGlobalTable';
import SuperAdminToggleCell from '../../pages/Users/SuperAdminToggleCell';
import tableStyles from '../../styles/components/table.module.css';
import styles from '../../styles/pages/index.module.css';

/** UserPublic extended with a flat `roles` array for the selected org context */
type UserWithRoles = UserPublic & { roles: Role[] };

export const Route = createFileRoute('/users/')({
  beforeLoad: ({ context, location }) => {
    const selectedOrganization = useAppStore.getState()?.selectedOrganization;

    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }

    if (!selectedOrganization) {
      // Set referrer path in sessionStorage before redirecting
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('unauthorized_referrer', location.pathname);
      }
      throw redirect({ to: '/unauthorized' });
    }

    // Org-aware role check for SuperAdmin or OrgAdmin using shared helper
    checkAndRedirectForOrgAdminAccess({
      context,
      location,
      selectedOrganization,
      adminRoles,
      redirect,
    });
  },
  component: RouteComponent,
});

function useOrgUsers(orgId: string, enabled: boolean) {
  return useQuery<UserWithRoles[]>({
    queryKey: userQueryKeys.orgUsers(orgId),
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<UserPublic[]>(`/orgs/${orgId}/users`);
      return (res ?? []).map(user => ({
        ...user,
        roles: user.allowed_roles?.[orgId] ?? [],
      }));
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    retry: (failureCount, error) => {
      if ((error as any)?.isUnauthorized || error?.name === 'UnauthorizedError' || (error as any)?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

const defaultColDef = {
  resizable: false,
};
const containerStyle = { padding: '0 var(--spacing-4) var(--spacing-4)' };

function RouteComponent() {
  // Protect this route - will redirect if user loses SuperAdmin role or switches org
  useRouteProtection(adminRoles);

  const ORG_ID = useAppStore(useShallow(state => state.selectedOrganization?.id));
  const { user: currentUser } = useAuthStatus();

  const { t: tGeneral } = useTranslation('general');
  const { t: tUsers } = useTranslation('users');
  const navigate = useNavigate();

  // `canManageSuperAdminRole` checks the static `user.is_super_admin` platform flag.
  // `isSuperAdmin()` from useRoles checks the *assumed* role in the selected org, which
  // updates reactively when the user switches their assumed role. Both must be true to
  // show the global SuperAdmin view — if the user assumes a lower role, this flips to false
  // immediately and the component re-renders to the standard org-scoped view.
  const { isSuperAdmin } = useRoles();
  const isGlobalSuperAdmin = canManageSuperAdminRole(currentUser) && !!ORG_ID && isSuperAdmin(ORG_ID);

  // Org-scoped users query — disabled for global SuperAdmins who use the server-side AG Grid table instead.
  // Passing an empty orgId when isGlobalSuperAdmin was true caused a malformed /orgs//users request.
  const { data: users, isLoading, error, refetch } = useOrgUsers(ORG_ID ?? '', !isGlobalSuperAdmin && !!ORG_ID);

  // Ref for the global SuperAdmin AG Grid instance
  const superAdminGridRef = useRef<AgGridReact<UserPublic> | null>(null);

  useEffect(() => {
    if (
      error &&
      ((error as any).isUnauthorized ||
        error.name === 'UnauthorizedError' ||
        (typeof error.message === 'string' && error.message.includes('not authorized')))
    ) {
      navigate({ to: '/unauthorized' });
    }
  }, [error, navigate]);

  const columns: ColDef<UserWithRoles>[] = useMemo(
    () => [
      {
        cellRenderer: ({ data }: { data: UserWithRoles }) => (
          <Stack alignItems="center" gap={4} orientation="horizontal">
            <Avatar size="xs2">{data.full_name}</Avatar>
            <Text as="span" className={getTextClassName('sm')}>
              {data.full_name}
            </Text>
          </Stack>
        ),
        field: 'full_name',
        headerName: tGeneral('name'),
        maxWidth: 240,
      },
      { field: 'email', headerName: tGeneral('email') },
      {
        cellClass: pageStyles.rolesCell,
        cellRenderer: ({ value }: { value: Role[] }) => {
          // Filter out SuperAdmin and sort the rest by hierarchy
          const filteredRoles = value.filter(role => role !== ROLE_HIERARCHY[0]);
          const sortedRoles = [...filteredRoles].toSorted(
            (a, b) => ROLE_HIERARCHY.indexOf(a) - ROLE_HIERARCHY.indexOf(b),
          );

          return (
            <Stack alignItems="center" fullWidth gap={2} orientation="horizontal">
              {sortedRoles.map(role => (
                <Badge className={pageStyles.badge} key={role} size="xs" variant="default">
                  {role}
                </Badge>
              ))}
            </Stack>
          );
        },
        field: 'roles',
        headerName: tGeneral('roles'),
      },

      {
        field: 'created_at',
        headerName: tGeneral('created_at'),
        hide: true,
        valueFormatter: ({ value }) => getDateAsString(new Date(value)),
      },
      // SuperAdmin toggle column — only rendered for global SuperAdmins
      ...(isGlobalSuperAdmin && currentUser
        ? [
            {
              cellClass: tableStyles.tableCellCenter,
              cellRenderer: ({ data }: { data: UserWithRoles }) => (
                <SuperAdminToggleCell currentUserId={currentUser.id} orgId={ORG_ID as string} user={data} />
              ),
              colId: 'super_admin_toggle',
              field: 'roles' as const,
              filter: false,
              width: 80,
              headerName: tUsers('super_admin_column_header'),
              sortable: false,
            },
          ]
        : []),
    ],
    [tGeneral, tUsers, isGlobalSuperAdmin, currentUser, ORG_ID],
  );

  // Promise-based refetch handler with toast feedback
  const handleRefetch = useCallback(async () => {
    const toastId = toast.loading(tUsers('refetching'));
    try {
      await refetch();
      toast.success(tUsers('refetch_success'), { id: toastId });
    } catch (err) {
      toast.error(tUsers('refetch_error') + (err instanceof Error && err.message ? `: ${err.message}` : ''), {
        id: toastId,
      });
    }
  }, [refetch, tUsers]);

  // Show loading only for non-SuperAdmin org-scoped query
  if (!isGlobalSuperAdmin && isLoading) return <FullLoading label={tUsers('loading')} />;

  if (!isGlobalSuperAdmin && error) return <ErrorMessage error={error} />;

  // ── Global SuperAdmin view: server-side table with POST /users/search ──
  if (isGlobalSuperAdmin && currentUser && ORG_ID) {
    return (
      <Stack fullHeight fullWidth gap={4}>
        <Stack alignItems="start" className={clsx(styles.titleRow, styles.organizationTitle)} justifyContent="center">
          <Stack alignItems="center" fullHeight gap={2} justifyContent="center" orientation="horizontal">
            <Text as={'div'} className={getTextClassName('lg')}>
              {tUsers('global_table_title')}
            </Text>
          </Stack>
          <Text as="p" className={getTextClassName('sm')}>
            {tUsers('global_table_subtitle')}
          </Text>
        </Stack>

        <SuperAdminGlobalTable currentUserId={currentUser.id} gridRef={superAdminGridRef} orgId={ORG_ID} />
      </Stack>
    );
  }

  // ── Standard org-scoped view ──
  return (
    <Stack fullHeight fullWidth gap={4}>
      <Stack alignItems="start" className={clsx(styles.titleRow, styles.organizationTitle)} justifyContent="center">
        <Stack alignItems="center" fullHeight gap={2} justifyContent="center" orientation="horizontal">
          <Text as={'div'} className={getTextClassName('lg')}>
            {tUsers('title')}
          </Text>

          <Tooltip
            content={tUsers('refetch_tooltip')}
            placement="right-start"
            trigger={
              <Button
                aria-label={tUsers('refetch_tooltip')}
                data-testid={'refetch-users-btn'}
                onPress={handleRefetch}
                visual="unstyled"
              >
                <RefreshCw size={16} />
              </Button>
            }
          />
        </Stack>
      </Stack>

      <Table
        containerStyle={containerStyle}
        columnDefs={columns}
        defaultColDef={defaultColDef}
        density="loose"
        domLayout="autoHeight"
        enableResponsiveResize={true}
        resizeDebounceMs={150}
        rowData={users ?? []}
        tableId="users-table"
      />
    </Stack>
  );
}
